import React, { useEffect, useState } from "react";
import { ThreeDots } from 'react-loader-spinner';
import axiosInstance from "../api/axiosInstance";
import API_ENDPOINTS from "../api/endpoints";
import { toast } from 'react-toastify';
import Chart from "../components/Chart/Chart";
import dayjs from "dayjs";
import PokerGamesTable from "../components/GameTable/PokerGamesTable";
import { DateRangePicker, createStaticRanges, defaultStaticRanges } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { FaRegCalendarAlt } from "react-icons/fa";

const GAME_TYPES = ["All", "Tournament", "Cash", "Satellite"];

const customStaticRanges = [
    {
        label: 'Last 6 Months',
        range: () => {
            const startDate = dayjs().subtract(6, 'month').startOf('month').toDate();
            const endDate = dayjs().endOf('month').toDate();
            return { startDate, endDate, label: 'Last 6 Months' };
        },
    },
    {
        label: 'This Year',
        range: () => {
            const startDate = dayjs().startOf('year').toDate();
            const endDate = dayjs().endOf('year').toDate();
            return { startDate, endDate, label: 'This Year' };
        },
    },
    {
        label: 'Custom',
        range: () => {
            const startDate = dayjs().startOf('day').toDate();
            const endDate = null;
            return { startDate, endDate };
        },
    }
];

const filteredDefaultStaticRanges = defaultStaticRanges.filter(
    range => !range.label.toLowerCase().includes("starting today")
);

const staticRanges = createStaticRanges([
    ...filteredDefaultStaticRanges,
    ...customStaticRanges
]);

export default function Profitability() {
    const [data, setData] = useState(null);
    const [bankrollChartData, setBankrollChartData] = useState([]);
    const [gamesData, setGamesData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [gameType, setGameType] = useState("All");
    const [range, setRange] = useState([
        {
            startDate: dayjs().subtract(6, 'month').startOf('month').toDate(),
            endDate: dayjs().endOf('month').toDate(),
            key: 'selection'
        }
    ]);
    const [showCalendar, setShowCalendar] = useState(false);
    const [tempRange, setTempRange] = useState(range);

    const fetchProfitability = async (fromDate, toDate, gameType) => {
        setLoading(true);
        try {
            const formattedFrom = dayjs(fromDate).format("YYYY-MM-DD");
            const formattedTo = dayjs(toDate).format("YYYY-MM-DD");
            const response = await axiosInstance.get(
                `${API_ENDPOINTS.PROFITABILITY}?fromDate=${formattedFrom}&toDate=${formattedTo}&gameType=${gameType}`
            );
            setData(response.data.data);
            const brChart = response.data.data.gameChart;
            const brFormatted = Object.entries(brChart).map(([date, value]) => ({
                date: dayjs(date).format("MMM D"),
                value,
            }));
            setBankrollChartData(brFormatted);
            setGamesData(response.data.data.gameTable)
            setLoading(false);
        } catch (err) {
            console.error("API error:", err);
            toast.error(err?.response?.data?.error || "Error fetching data");
            setLoading(false);
        }
    };

    useEffect(() => {
        if (range[0].startDate && range[0].endDate) {
            fetchProfitability(range[0].startDate, range[0].endDate, gameType);
        } 
    }, [gameType, range]);

    const handlechange = (ranges) => {
        const { startDate, endDate } = ranges.selection;
        setTempRange([{ startDate, endDate, key: 'selection' }]);
    };

    const applyRange = () => {
        setRange(tempRange);
        setShowCalendar(false);
        fetchProfitability(tempRange[0].startDate, tempRange[0].endDate);
    };

    const closeCalendar = () => {
        setShowCalendar(false);
        setTempRange(range); // Reset to last saved
    };

    if (!data || loading) return <div className="text-white p-6">
        <ThreeDots
            visible={true}
            height="80"
            width="80"
            color="#1976d2"
            radius="9"
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClass="dots-loading additional-class"
        />
    </div>;


    return (
        <div className="p-6 text-white">

            {/* Filters */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6 w-full">
                <h2 className="text-3xl font-bold ">Performance</h2>
                <div className="relative w-full md:w-auto">
                    <button
                        className="flex items-center border border-border bg-black text-white px-3 py-2 rounded w-full md:w-[260px] justify-between"
                        onClick={() => setShowCalendar(!showCalendar)}
                    >
                        <span>
                            {`${dayjs(range[0].startDate).format("MMM DD, YYYY")} - ${dayjs(range[0].endDate).format("MMM DD, YYYY")}`}
                        </span>
                        <FaRegCalendarAlt className="ml-2" />
                    </button>

                    {showCalendar && (
                        <div className="absolute z-[99999] mt-2 bg-[#181818] border border-border rounded shadow-lg left-0 lg:-left-5 transform lg:-translate-x-1/2 ">
                            <DateRangePicker
                                editableDateInputs={true}
                                onChange={handlechange}
                                moveRangeOnFirstSelection={false}
                                staticRanges={staticRanges}
                                ranges={tempRange}
                                maxDate={new Date()}
                                rangeColors={["#3b82f6"]}
                                className="text-black customrange-selector"
                            />
                            <div className="flex justify-end gap-2 p-3 border-t border-border bg-[#121212]">
                                <button
                                    onClick={closeCalendar}
                                    className="px-8 py-2  border rounded text-sm text-gray-300 hover:bg-[#222]"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={applyRange}
                                    className="px-8 py-2 bg-secondary border border-secondary text-white rounded text-sm hover:opacity-75"
                                >
                                    Fetch
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Performance Chart */}
            <div className="border border-border  bg-[#0a0a0a] p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Performance Chart</h3>
                {/* Chart filter buttons */}
                <div className="mb-4 flex justify-end w-full">
                    {GAME_TYPES.map(type => (
                        <button
                            key={type}
                            className={`md:px-4 md:py-2 px-2 py-1 text-sm rounded mx-1 hover:bg-[#181818] ${gameType === type ? "bg-blue-600 text-white" : " text-gray-300"}`}
                            onClick={() => setGameType(type)}
                        >
                            {type}
                        </button>
                    ))}
                </div>
                <Chart
                    chartData={bankrollChartData}
                />
            </div>
            <PokerGamesTable
                games={gamesData}
            />
        </div>
    );
}
